import { escapeHtml, formatDate, formatCurrency } from '../../lib/helpers';
import { toYmd } from '../availability-calendar/pricing';
import type { OfferDetails } from '../../lib/types';

function nextDayYmd(ymd: string): string {
    const [y, m, d] = ymd.split('-').map(Number);
    return toYmd(new Date(y, m - 1, d + 1));
}

const CHECK_SVG =
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none">' +
    '<path d="M1.5 5L4 7.5 8.5 2" stroke="white" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * Build the info-card HTML shown below the offer list after selection.
 */
export function groupDetailsHtml(
    ids: string[],
    cache: Record<string, OfferDetails>,
    emptyMsg: string
): string {
    let html = '';
    for (const id of ids) {
        const offer = cache[id];
        if (!offer) continue;

        const row = (label: string, value: string, style = '') =>
            `<div class="cc-accomm-info-row">` +
            `<span>${escapeHtml(label)}</span>` +
            `<span${style ? ` style="${style}"` : ''}>${escapeHtml(value)}</span>` +
            `</div>`;

        html += `<div class="cc-accomm-info">`;
        html += `<div class="cc-accomm-info-title">${escapeHtml(offer.name)}</div>`;
        if (offer.city)            html += row('City',      offer.city);
        if (offer.address)         html += row('Address',   offer.address);
        if (offer.phone)           html += row('Phone',     offer.phone);
        if (offer.email)           html += row('Email',     offer.email, 'color:var(--cc-brand-dark)');
        if (offer.entryPlace)      html += row('Entry',     offer.entryPlace);
        if (offer.transportNumber) html += row('Transport', offer.transportNumber);
        html += `</div>`;
    }
    return html || `<p class="text-muted small">${escapeHtml(emptyMsg)}</p>`;
}

export interface PricesHtmlResult {
    html: string;
    hasPrices: boolean;
    hasFilteredOut: boolean;
}

/**
 * Build the price-card HTML.
 * Sub-range groups (Accommodation, Insurance): one date-range picker card per OfferUnit.
 * Flat-seats groups (Transport): one checkbox card per OfferPrice (unchanged behaviour).
 * All inputs bind into Selections[unitId].* for server-side model binding.
 */
export function groupPricesHtml(
    ids: string[],
    cache: Record<string, OfferDetails>,
    prevChecked: Set<string>,
    dateFilter: { from: string; to: string } | undefined,
    emptyMsg: string,
    group?: string,
    allowsSubRange?: boolean,
    rateUnit?: string,
    pricingMode?: string,
    fromLabel = 'From',
    toLabel = 'To'
): PricesHtmlResult {
    let html = '';
    let hasPrices = false;
    let hasFilteredOut = false;

    for (const id of ids) {
        const offer = cache[id];
        if (!offer?.offerUnits) continue;

        html += `<div class="cc-offer-cards-group">`;
        html += `<div class="cc-offer-cards-label">${escapeHtml(offer.name)}</div>`;

        for (const unit of offer.offerUnits) {
            if (!unit.offerPrices?.length) continue;

            const unitType = unit.offerUnitTypeName || unit.type || '';
            const typePart = unitType
                ? ` <span class="cc-offer-card-type">· ${escapeHtml(unitType)}</span>`
                : '';

            if (allowsSubRange) {
                // Sub-range: one date-picker card per unit
                hasPrices = true;
                const prices = unit.offerPrices;
                const minDate = prices.reduce<string>((m, p) => (p.from && (!m || p.from < m)) ? p.from : m, '');
                const maxDate = prices.reduce<string>((m, p) => (p.to   && (!m || p.to   > m)) ? p.to   : m, '');
                const minStr = minDate ? minDate.substring(0, 10) : '';
                const maxStr = maxDate ? maxDate.substring(0, 10) : '';
                const toMaxStr = pricingMode === 'per-night' && maxStr ? nextDayYmd(maxStr) : maxStr;

                html += `<div class="cc-unit-selection-card border rounded p-2 mb-2" data-unit-id="${unit.id}">`;
                html += `<input type="hidden" name="Selections.index" value="${unit.id}" />`;
                html += `<input type="hidden" name="Selections[${unit.id}].OfferUnitId" value="${unit.id}" />`;
                html += `<div class="d-flex justify-content-between align-items-start mb-1">`;
                html += `<div class="cc-offer-card-name">${escapeHtml(unit.name)}${typePart}</div>`;
                html += `<div class="cc-offer-card-price" data-line-total="0">–</div>`;
                html += `</div>`;
                html += `<div class="cc-unit-date-row">`;
                html += `<div class="cc-date-field">`;
                html += `<label class="cc-date-label">${escapeHtml(fromLabel)}</label>`;
                html +=
                    `<input type="date" class="form-control form-control-sm sub-range-from-picker cc-date-input"` +
                    ` name="Selections[${unit.id}].From"` +
                    (minStr ? ` min="${minStr}"` : '') +
                    (maxStr ? ` max="${maxStr}"` : '') +
                    ` data-unit-id="${unit.id}" />`;
                html += `</div>`;
                html += `<span class="cc-date-separator text-muted small">–</span>`;
                html += `<div class="cc-date-field">`;
                html += `<label class="cc-date-label">${escapeHtml(toLabel)}</label>`;
                html +=
                    `<input type="date" class="form-control form-control-sm sub-range-to-picker cc-date-input"` +
                    ` name="Selections[${unit.id}].To"` +
                    (minStr ? ` min="${minStr}"` : '') +
                    (toMaxStr ? ` max="${toMaxStr}"` : '') +
                    ` data-unit-id="${unit.id}" />`;
                html += `</div>`;
                html += `<button type="button" class="btn btn-sm btn-link cc-toggle-calendar d-none d-lg-inline-flex align-items-center gap-1 ms-1" data-unit-id="${unit.id}" aria-expanded="false">`;
                html += `<svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;
                html += `</button>`;
                html += `</div>`;
                html += `<div class="cc-unit-calendar d-none mt-2" data-unit-id="${unit.id}" data-offer-id="${id}"></div>`;
                html += `<div class="mt-1">`;
                for (const price of prices) {
                    html +=
                        `<div class="cc-offer-card-sub">` +
                        `${formatDate(price.from)} – ${formatDate(price.to)}: ` +
                        `${formatCurrency(price.price)}` +
                        (rateUnit ? ` ${escapeHtml(rateUnit)}` : '') +
                        `</div>`;
                }
                html += `</div>`;
                html += `</div>`;
            } else {
                // Flat-seats: per-price checkboxes scoped under the unit's Selection
                const filtered = dateFilter
                    ? unit.offerPrices.filter(p => {
                        if (!p.from || !p.to) return false;
                        return p.to >= dateFilter.from && p.from <= dateFilter.to;
                    })
                    : unit.offerPrices;

                if (dateFilter && filtered.length < unit.offerPrices.length) hasFilteredOut = true;
                if (!filtered.length) continue;

                html += `<div class="cc-unit-prices" data-unit-id="${unit.id}">`;
                html += `<input type="hidden" name="Selections.index" value="${unit.id}" />`;
                html += `<input type="hidden" name="Selections[${unit.id}].OfferUnitId" value="${unit.id}" />`;

                for (const price of filtered) {
                    hasPrices = true;
                    const cbId    = `offer-price-cb-${price.id}`;
                    const checked = prevChecked.has(String(price.id));
                    const groupAttr = group ? ` data-offer-group="${escapeHtml(group)}"` : '';

                    const subParts: string[] = [
                        `${formatDate(price.from)} – ${formatDate(price.to)}`,
                    ];
                    if (unit.classification) subParts.push(`Capacity: ${escapeHtml(unit.classification)}`);
                    if (price.numberOfSeats)  subParts.push(`${price.numberOfSeats} seats`);

                    const rateLabel = rateUnit
                        ? ` <span class="fw-normal text-muted small">${escapeHtml(rateUnit)}</span>`
                        : '';

                    html += `<label class="cc-offer-card${checked ? ' cc-offer-card--checked' : ''}" for="${cbId}">`;
                    html +=
                        `<input id="${cbId}" class="offer-price-cb" type="checkbox"` +
                        ` name="Selections[${unit.id}].SelectedOfferPriceIds" value="${price.id}"` +
                        ` data-price="${price.price}"` +
                        ` data-from="${price.from || ''}" data-to="${price.to || ''}"` +
                        `${groupAttr}${checked ? ' checked' : ''} />`;
                    html += `<div class="cc-offer-cb-box">${checked ? CHECK_SVG : ''}</div>`;
                    html += `<div class="cc-offer-card-left">`;
                    html += `<div class="cc-offer-card-name">${escapeHtml(unit.name)}${typePart}</div>`;
                    html += `<div class="cc-offer-card-sub">${subParts.join(' · ')}</div>`;
                    html += `</div>`;
                    html += `<div class="cc-offer-card-price">${formatCurrency(price.price)}${rateLabel}</div>`;
                    html += `</label>`;
                }

                html += `</div>`;
            }
        }

        html += `</div>`;
    }

    if (!hasPrices) {
        if (hasFilteredOut && dateFilter) {
            html =
                `<p class="text-muted small mb-0">` +
                `No prices available for the selected accommodation dates ` +
                `(${formatDate(dateFilter.from)} – ${formatDate(dateFilter.to)}).` +
                `</p>`;
        } else {
            html = `<p class="text-muted small mb-0">${escapeHtml(emptyMsg)}</p>`;
        }
    }

    return { html, hasPrices, hasFilteredOut };
}
